<template>
  <eg-b-sidebar
    id="students-sidebar"
    :visible="localVisible"
    :width="sidebarWidth"
    right
    shadow
    no-header
    backdrop
    backdrop-variant="transparent"
    sidebar-class="bg-white students-sidebar"
    @hidden="onHidden"
  >
    <template #default="{ hide }">
      <div class="d-flex flex-column h-100">
        <eg-modal-header
          :closable="true"
          :title="title || $t('common.studentsLibrary')"
          @onClose="hide"
        >
          <eg-button
            v-if="isSelectMode"
            type="secondary"
            icon="plus-add-more-maximize"
            @click="openCreateModal"
          >
            {{ $t("common.createNewStudent") }}
          </eg-button>
        </eg-modal-header>

        <div
          class="p-3 flex-grow-1 students-sidebar__content"
          :class="{ 'students-sidebar__content--metrics': !isSelectMode }"
        >
          <div class="py-3 d-flex justify-content-start flex-gap-1">
            <eg-dropdown
              v-if="isSelectMode"
              :text="
                selectedPlatform
                  ? selectedPlatform.label
                  : $t('common.platform')
              "
              :class="
                selectedPlatform
                  ? 'students-sidebar__platform-select'
                  : 'students-sidebar__platform-select all'
              "
            >
              <eg-b-dropdown-item
                v-for="item in platformOptions"
                :key="item.key"
                :active="selectedPlatform?.key === item.key"
                @click="selectedPlatform = item.key === 'all' ? null : item"
              >
                <div class="students-sidebar__dropdown-item">
                  <span class="text-truncate paragraph-s">{{
                    item.label
                  }}</span>
                </div>
              </eg-b-dropdown-item>
            </eg-dropdown>

            <eg-input
              :key="searchKey"
              v-model="search"
              variant="search"
              :placeholder="$t('common.search')"
              class="students-sidebar__search"
            />
          </div>

          <div class="students-sidebar__list">
            <eg-b-overlay :show="isLoadingData" no-wrap>
              <template #overlay>
                <eg-b-spinner small variant="info" />
              </template>
            </eg-b-overlay>

            <div
              v-if="!isLoadingData && filteredStudents.length === 0"
              class="students-sidebar__empty"
            >
              {{ $t("common.noStudentsFound") }}
            </div>

            <template v-if="isSelectMode">
              <div
                v-for="student in filteredStudents"
                :key="student.id"
                class="students-sidebar__card"
              >
                <div class="d-flex flex-column flex-grow-1 p-3 text-truncate">
                  <span class="semi-bold text-truncate">
                    {{ student.firstName }} {{ student.lastName }}
                  </span>
                </div>
                <div
                  v-if="student.platform"
                  class="students-sidebar__platform-tag p-3"
                >
                  <img
                    v-if="platformIconMap[student.platform]"
                    :src="platformIconMap[student.platform]"
                    width="20"
                    class="students-sidebar__platform-icon"
                  />
                  <div class="text-truncate">
                    {{ platformLabelMap[student.platform] || student.platform }}
                  </div>
                </div>
                <div
                  class="d-flex justify-content-end p-3 students-sidebar__card-actions"
                >
                  <span
                    v-b-tooltip.hover
                    :title="
                      !isEssayGraderStudent(student)
                        ? $t('common.lmsCannotRemove')
                        : null
                    "
                  >
                    <eg-button
                      type="secondary"
                      icon="delete"
                      size="small"
                      :disabled="!isEssayGraderStudent(student)"
                      @click="deleteStudent(student)"
                    />
                  </span>
                  <span
                    v-b-tooltip.hover
                    :title="
                      !isEssayGraderStudent(student)
                        ? $t('common.lmsCannotEdit')
                        : null
                    "
                  >
                    <eg-button
                      type="secondary"
                      icon="pen-edit"
                      size="small"
                      :disabled="!isEssayGraderStudent(student)"
                      @click="editStudent(student)"
                    />
                  </span>
                  <eg-button
                    type="primary"
                    size="small"
                    @click="assignExisting(student)"
                  >
                    {{ $t("common.select") }}
                  </eg-button>
                </div>
              </div>
            </template>

            <template v-else>
              <div
                v-for="student in filteredStudents"
                :key="student.id"
                class="students-sidebar__row"
                @click="onMetricsStudentClick(student)"
              >
                <div class="students-sidebar__avatar">
                  {{ initials(student) }}
                </div>
                <span class="students-sidebar__name paragraph-s">
                  {{ student.firstName }} {{ student.lastName }}
                </span>
                <span class="students-sidebar__score paragraph-s">
                  {{
                    student.avgGrade != null
                      ? Math.round(student.avgGrade) + "%"
                      : "—"
                  }}
                </span>
              </div>
            </template>
          </div>
        </div>

        <div v-if="!isSelectMode" class="students-sidebar__footer paragraph-s">
          {{ $t("common.studentsShown", { count: filteredStudents.length }) }}
        </div>
      </div>
    </template>
  </eg-b-sidebar>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { attachStudentToEssay } from "@/api/requests/students";
import { LMS_PROVIDERS } from "@/constants";
import egLogoTransparent from "@/assets/eg-logo-tiny-transparent.png";
import EgModalHeader from "@/components/global/eg/modals/eg_modal_header.vue";
import CreateStudentModal from "@/components/students/create_student_modal.vue";
import { studentInitials } from "@/utils/metricsUtils";
import metricsViewMixin from "@/mixins/metricsViewMixin.js";

const PLATFORM_OPTIONS = [
  {
    label: "EssayGrader",
    key: "essaygrader",
    icon: egLogoTransparent,
    createGradeKeys: ["upload"],
  },
  {
    label: "Google Classroom",
    key: "google",
    icon: LMS_PROVIDERS.google.icon,
    createGradeKeys: ["google"],
  },
  {
    label: "Canvas",
    key: "canvas",
    icon: LMS_PROVIDERS.canvas.icon,
    createGradeKeys: ["canvas", "canvas-manual"],
  },
  {
    label: "Schoology",
    key: "schoology",
    icon: LMS_PROVIDERS.schoology.icon,
    createGradeKeys: ["schoology"],
  },
];

export default {
  name: "StudentsSidebar",

  components: { EgModalHeader },

  mixins: [metricsViewMixin],

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // 'select' — attach student to essay (default)
    // 'metrics' — pick student to navigate to their metrics page
    mode: {
      type: String,
      default: "select",
    },
    title: {
      type: String,
      default: null,
    },

    // --- select mode ---
    essay: {
      type: Object,
      default: null,
    },

    // --- metrics mode ---
    // students from the metrics API: [{id, firstName, lastName, avgGrade}]
    // used as source when student is not found in the store
    metricsStudents: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    searchKey: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      search: "",
      selectedPlatform: null,
    };
  },

  computed: {
    ...mapGetters("students", ["students"]),
    ...mapState("students", { storeLoading: "isLoading" }),

    localVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },

    isSelectMode() {
      return this.mode === "select";
    },

    sidebarWidth() {
      return this.isSelectMode ? "680px" : "460px";
    },

    isLoadingData() {
      return this.isSelectMode ? this.storeLoading : this.isLoading;
    },

    platformOptions() {
      const createGrade =
        this.$store.getters["config/features"]?.createGrade || [];
      const enabled = PLATFORM_OPTIONS.filter((platform) =>
        platform.createGradeKeys.some((key) => createGrade.includes(key))
      );
      return [{ label: this.$t("common.all"), key: "all" }, ...enabled];
    },

    platformLabelMap() {
      return Object.fromEntries(
        PLATFORM_OPTIONS.map((platform) => [platform.key, platform.label])
      );
    },

    platformIconMap() {
      return Object.fromEntries(
        PLATFORM_OPTIONS.map((platform) => [platform.key, platform.icon])
      );
    },

    // metrics mode: merge store data with metricsStudents (store is source of truth for name/platform)
    enrichedStudents() {
      if (!this.metricsStudents.length) {
        return [];
      }
      const storeMap = Object.fromEntries(
        this.students.map((student) => [student.id, student])
      );
      return this.metricsStudents.map((metricsStudent) => ({
        ...metricsStudent,
        ...(storeMap[metricsStudent.id] || {}),
        avgGrade: metricsStudent.avgGrade,
      }));
    },

    sourceList() {
      return this.isSelectMode ? this.students : this.enrichedStudents;
    },

    filteredStudents() {
      let list = this.sourceList;

      if (this.isSelectMode && this.selectedPlatform) {
        list = list.filter(
          (student) => student.platform === this.selectedPlatform.key
        );
      }

      const query = this.search.trim().toLowerCase();
      if (!query) {
        return list;
      }
      return list.filter((student) =>
        `${student.firstName} ${student.lastName || ""}`
          .toLowerCase()
          .includes(query)
      );
    },
  },

  watch: {
    searchKey() {
      this.search = "";
    },
  },

  methods: {
    onHidden() {
      this.$emit("input", false);
      this.search = "";
      this.selectedPlatform = null;
    },

    initials(student) {
      return studentInitials(student);
    },

    // --- select mode methods ---

    openCreateModal() {
      this.$egModal.show("create-student-modal", {
        component: CreateStudentModal,
        props: {
          essayId: this.essay?.id,
        },
        on: {
          studentCreated: () => {
            this.$egModal.hide("create-student-modal");
          },
          studentCreatedAndSelected: (essay) => {
            this.$egModal.hide("create-student-modal");
            this.$emit("studentAssigned", essay);
            this.localVisible = false;
          },
        },
      });
    },

    isEssayGraderStudent(student) {
      return !student.platform || student.platform === "essaygrader";
    },

    editStudent(student) {
      this.$egModal.show("create-student-modal", {
        component: CreateStudentModal,
        props: { student },
        on: {
          studentSaved: () => {
            this.$egModal.hide("create-student-modal");
            this.$emit("studentUpdated");
          },
        },
      });
    },

    async deleteStudent(student) {
      const studentName = `${student.firstName} ${student.lastName}`.trim();
      const confirmed = await this.$showDeleteConfirmationModal({
        title: this.$t("common.removeStudentTitle", { name: studentName }),
        message: this.$t("common.deleteStudentMessage", { name: studentName }),
      });
      if (!confirmed) return;
      try {
        await this.$store.dispatch("students/delete", student.id);
      } catch (e) {
        this.$showToastError(e.response?.data?.error);
      }
    },

    async assignExisting(student) {
      if (!this.essay) return;
      try {
        const { data } = await attachStudentToEssay({
          essayId: this.essay.id,
          studentId: student.id,
        });
        this.$emit("studentAssigned", data);
        this.localVisible = false;
      } catch (e) {
        this.$showToastError(e.response?.data?.error);
      }
    },

    // --- metrics mode methods ---

    onMetricsStudentClick(student) {
      this.localVisible = false;
      this.goToStudent(student.studentId, this.$route.params.classId);
    },
  },
};
</script>

<style lang="scss" scoped>
.students-sidebar {
  &__content {
    background-color: var(--color-surface-neutral-container-1);
    overflow-y: auto;

    &--metrics {
      background-color: #fff;
    }
  }

  &__platform-select {
    width: 180px;
    max-width: 180px;

    &::v-deep {
      .btn {
        background-color: var(--color-surface-neutral-default);
        font-size: var(--typography-font-size-fs-300);
      }
    }

    &.all::v-deep {
      .btn {
        color: var(--color-text-neutral-disabled);
      }
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  // select mode card
  &__card {
    display: flex;
    gap: 8px;
    align-items: center;
    background-color: var(--color-surface-neutral-default);
    border: 1px solid var(--color-stroke-neutral-default);
    border-radius: var(--radius-rd-300);
  }

  &__card-actions {
    gap: 0.75rem;
  }

  &__platform-icon {
    vertical-align: middle;
    flex-shrink: 0;
  }

  &__platform-tag {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 150px;
    min-width: 150px;
  }

  &__dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  // metrics mode row
  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 4px;
    cursor: pointer;
    border-radius: var(--radius-rd-300, 8px);
    transition: background 0.1s;

    &:hover {
      background: var(--color-surface-neutral-container-1, #f7f9fa);
    }
  }

  &__avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: var(--color-surface-primary-light-1, #ede9fe);
    color: var(--color-text-primary-default, #7c3aed);
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-700, 700);
    flex-shrink: 0;
  }

  &__name {
    flex: 1;
    color: var(--color-text-neutral-on-white, #1a2330);
    font-weight: var(--typography-font-weight-fw-500, 500);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__score {
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white, #1a2330);
    flex-shrink: 0;
  }

  &__empty {
    color: var(--color-text-neutral-secondary);
    font-size: var(--typography-font-size-fs-400);
    text-align: center;
    padding: 32px 0;
  }

  &__footer {
    padding: 12px 24px;
    border-top: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
    color: var(--color-text-neutral-secondary, #566370);
    background-color: var(--color-surface-neutral-container-1);
    flex-shrink: 0;
  }
  &__search {
    flex: 1;
    min-width: 0;
  }
}
</style>
